import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Inject, Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  IRootState,
  OnboardingComponentStore,
  UserSelectors,
} from '@mobile-data-access-stores';
import { IUserService, USER_SERVICE } from '@mobile-data-access-services';
import {
  OnboardingBeginSteps,
  OnboardingRequestHeaderAttributes,
  OnboardingStatuses,
} from '@mobile-data-access-enums';
import { IUserProfile } from '@mobile-data-access-interfaces';

@Injectable()
export class OnboardingGuard implements CanActivate {
  //#region Constructor

  public constructor(
    @Inject(USER_SERVICE)
    protected readonly _userService: IUserService,
    protected readonly _store: Store<IRootState>,
    protected readonly _componentStore: OnboardingComponentStore
  ) {}

  //#endregion

  //#region Methods

  public canActivate(
    activatedRouteSnapshot: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this._store.select(UserSelectors.profile).pipe(
      take(1),
      switchMap((profile) => {
        if (!profile) {
          return this._userService
            .getProfileAsync()
            .pipe(switchMap((data) => this.resolveAsync(data)));
        }

        return this.resolveAsync(profile);
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  //#endregion

  //#region Methods

  public resolveAsync(profile: IUserProfile): Observable<boolean> {
    const manualOnboardingRequired =
      profile.onboarding.status === OnboardingStatuses.ONBOARDING &&
      profile.acceptTermCondition;

    this._componentStore.updateValue(
      'acceptTermCondition',
      profile.acceptTermCondition
    );
    this._componentStore.updateValue('data', profile.onboarding);
    this._componentStore.updateValue(
      'manualOnboardingRequired',
      manualOnboardingRequired
    );
    if (
      (profile.onboarding.status === OnboardingStatuses.ONBOARDING &&
        profile.acceptTermCondition) ||
      profile.onboarding.status === OnboardingStatuses.ONBOARDED
    ) {
      const isOnboarded =
        profile.onboarding.status === OnboardingStatuses.ONBOARDED;
      if (isOnboarded) {
        this._componentStore.updateValue(
          'key',
          OnboardingBeginSteps.ENTER_PIN_CODE
        );
      } else {
        if (profile.acceptTermCondition) {
          if (
            sessionStorage.getItem(OnboardingRequestHeaderAttributes.X_PIN_CODE)
          ) {
            this._componentStore.loadDiseaseAsync(() =>
              this._componentStore.updateValue(
                'key',
                OnboardingBeginSteps.CHOOSE_YOUR_DISEASE
              )
            );
          } else {
            this._componentStore.updateValue(
              'key',
              OnboardingBeginSteps.ENTER_PIN_CODE
            );
          }

          return of(true);
        } else {
          this._componentStore.updateValue(
            'key',
            OnboardingBeginSteps.TERMS_AND_CONDITIONS
          );
        }
      }
    }
    return of(true);
  }

  //#endregion
}
